import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import { useLocations } from '../../../hooks/useLocations'; // ADD THIS IMPORT
import '../../../styles/PermissionsManagement.css';

interface OrganizationPermissions {
  read?: string[];
  write?: string[];
  admin?: string[];
}

interface UserWithPermissions {
  email: string;
  permissions: string[];
  organizationRoles: {
    read: boolean;
    write: boolean;
    admin: boolean;
  };
}

interface PermissionsManagementProps {
  selectedOrganizationId: string;
  onBackToOrganizations: () => void;
  onNavigateToApps: () => void;
  onNavigateToAdmin?: () => void;
  onNavigateToPermissions: () => void;
  db: Firestore;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

const PermissionsManagement: React.FC<PermissionsManagementProps> = ({
  selectedOrganizationId,
  onBackToOrganizations,
  onNavigateToApps,
  onNavigateToAdmin,
  onNavigateToPermissions,
  db,
  functions,
  onError,
  onSuccess,
  setLoading
}) => {
  const { currentUser } = useAuth();
  const { getOrganizationName, getOrganizationById } = useLocations(); // ADD THIS LINE
  const [organizationPermissions, setOrganizationPermissions] = useState<OrganizationPermissions>({});
  const [usersWithPermissions, setUsersWithPermissions] = useState<UserWithPermissions[]>([]);
  const [loading, setLocalLoading] = useState(true);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRoles, setNewUserRoles] = useState<{[key: string]: boolean}>({
    read: false,
    write: false,
    admin: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cloud function references
  const getDocumentPermissionsFunc = httpsCallable(functions, 'getDocumentPermissions');
  const manageDocumentPermissionsFunc = httpsCallable(functions, 'manageDocumentPermissions');

  // UPDATED: Get organization name and info using the hook
  const getDisplayOrganizationName = (): string => {
    const orgName = getOrganizationName(selectedOrganizationId);
    return formatOrganizationName(orgName);
  };

  const getOrganizationInfo = () => {
    const orgConfig = getOrganizationById(selectedOrganizationId);
    return {
      name: formatOrganizationName(getOrganizationName(selectedOrganizationId)),
      location: orgConfig?.locationName || 'Unknown Location',
      id: selectedOrganizationId
    };
  };

  // Helper function to format organization names nicely
  const formatOrganizationName = (name: string): string => {
    // Convert from "DINEUKFINALMILE" to "Dine UK Final Mile"
    if (name === 'DINEUKFINALMILE') return 'Dine UK Final Mile';
    if (name === 'DINEUSA99') return 'Dine USA 99';
    
    // Generic formatting for other org names
    return name
      .toLowerCase()
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add spaces before capitals
      .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
  };

  // Load organization permissions and users
  const loadOrganizationData = async () => {
    if (!selectedOrganizationId || !currentUser) {
      setLocalLoading(false);
      return;
    }

    setLocalLoading(true);
    setLoading(true);
    
    try {
      // Get organization permissions using generic function
      const result = await getDocumentPermissionsFunc({ 
        collectionId: 'organizations',
        documentId: selectedOrganizationId 
      });

      // @ts-ignore
      if (result.data && result.data.success) {
        // @ts-ignore
        const permissions = result.data.permissions as OrganizationPermissions;
        setOrganizationPermissions(permissions);

        // Get all unique user emails from permissions
        const allUserEmails = new Set<string>();
        
        Object.values(permissions).forEach((userList) => {
          if (Array.isArray(userList)) {
            userList.forEach((email) => allUserEmails.add(email));
          }
        });

        // Fetch user data for all users in the organization
        if (allUserEmails.size > 0) {
          // Create user objects from the permissions data only
          const userResults: UserWithPermissions[] = Array.from(allUserEmails).map((email) => ({
            email,
            permissions: [], // We don't need to access individual user documents
            organizationRoles: {
              read: permissions.read?.includes(email) || false,
              write: permissions.write?.includes(email) || false,
              admin: permissions.admin?.includes(email) || false,
            }
          }));
          
          // Sort users by email
          userResults.sort((a, b) => a.email.localeCompare(b.email));
          
          setUsersWithPermissions(userResults);
        } else {
          setUsersWithPermissions([]);
        }
      } else {
        // @ts-ignore
        onError(result.data?.message || 'Failed to load organization permissions');
      }

    } catch (err: any) {
      console.error('Error loading organization data:', err);
      
      if (err.code === 'unauthenticated') {
        onError('Authentication failed. Please try logging out and back in.');
      } else if (err.code === 'permission-denied') {
        onError('Permission denied. You may not have admin access to this organization.');
      } else {
        onError(err.message || 'Failed to load organization data');
      }
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizationData();
  }, [selectedOrganizationId, currentUser]);

  // Handle role toggle
  const handleRoleToggle = async (userEmail: string, role: 'read' | 'write' | 'admin', currentlyHasRole: boolean) => {
    setIsSubmitting(true);
    
    try {
      const operation = currentlyHasRole ? 'revoke' : 'grant';
      
      const result = await manageDocumentPermissionsFunc({
        collectionId: 'organizations',
        documentId: selectedOrganizationId,
        userEmail: userEmail,
        role: role,
        operation: operation
      });

      // @ts-ignore
      if (result.data && result.data.success) {
        // @ts-ignore
        onSuccess(result.data.message);
        // Reload data to reflect changes
        await loadOrganizationData();
      } else {
        // @ts-ignore
        onError(result.data?.message || 'Failed to update user permissions');
      }
    } catch (err: any) {
      console.error('Error updating user permissions:', err);
      onError(err.message || 'Failed to update user permissions');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle add user
  const handleAddUser = async () => {
    if (!newUserEmail.trim()) {
      onError('Please enter a valid email address');
      return;
    }

    if (!newUserEmail.includes('@')) {
      onError('Please enter a valid email address');
      return;
    }

    const selectedRoles = Object.entries(newUserRoles).filter(([_, selected]) => selected);
    if (selectedRoles.length === 0) {
      onError('Please select at least one role for the user');
      return;
    }

    setIsSubmitting(true);

    try {
      // Add each selected role using generic function
      for (const [role, _] of selectedRoles) {
        const result = await manageDocumentPermissionsFunc({
          collectionId: 'organizations',
          documentId: selectedOrganizationId,
          userEmail: newUserEmail.trim(),
          role: role as 'read' | 'write' | 'admin',
          operation: 'grant'
        });

        // @ts-ignore
        if (!result.data || !result.data.success) {
          // @ts-ignore
          throw new Error(result.data?.message || `Failed to grant ${role} permission`);
        }
      }

      onSuccess(`User ${newUserEmail} added successfully with ${selectedRoles.length} role${selectedRoles.length > 1 ? 's' : ''}`);
      
      // Reset form
      setNewUserEmail('');
      setNewUserRoles({ read: false, write: false, admin: false });
      setShowAddUserForm(false);
      
      // Reload data
      await loadOrganizationData();

    } catch (err: any) {
      console.error('Error adding user:', err);
      onError(err.message || 'Failed to add user');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle remove all roles for a user
  const handleRemoveUser = async (userEmail: string, userRoles: {read: boolean, write: boolean, admin: boolean}) => {
    setIsSubmitting(true);
    
    try {
      // Remove all roles for this user
      const rolesToRemove = Object.entries(userRoles).filter(([_, hasRole]) => hasRole);
      
      for (const [role, _] of rolesToRemove) {
        const result = await manageDocumentPermissionsFunc({
          collectionId: 'organizations',
          documentId: selectedOrganizationId,
          userEmail: userEmail,
          role: role as 'read' | 'write' | 'admin',
          operation: 'revoke'
        });

        // @ts-ignore
        if (!result.data || !result.data.success) {
          // @ts-ignore
          throw new Error(result.data?.message || `Failed to revoke ${role} permission`);
        }
      }

      onSuccess(`All permissions removed for ${userEmail}`);
      
      // Reload data
      await loadOrganizationData();

    } catch (err: any) {
      console.error('Error removing user:', err);
      onError(err.message || 'Failed to remove user permissions');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role: 'read' | 'write' | 'admin'): string => {
    switch (role) {
      case 'read':
        return 'permissions-badge-read';
      case 'write':
        return 'permissions-badge-write';
      case 'admin':
        return 'permissions-badge-admin';
      default:
        return 'permissions-badge-none';
    }
  };

  // Get user's highest role for sorting
  const getUserHighestRole = (user: UserWithPermissions): number => {
    if (user.organizationRoles.admin) return 3;
    if (user.organizationRoles.write) return 2;
    if (user.organizationRoles.read) return 1;
    return 0;
  };

  const orgInfo = getOrganizationInfo();

  return (
    <>
      <div className="applist-content-header">
        <h1 className="applist-page-title">Organization Permissions</h1>
        <div className="permissions-header-actions">
          <div className="permissions-header-info">
            {/* UPDATED: Show formatted organization name and location */}
            <span className="permissions-org-name">{orgInfo.name}</span>
            <span className="permissions-user-count">
              {usersWithPermissions.length} user{usersWithPermissions.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button
            type="button"
            className="permissions-add-user-btn"
            onClick={() => setShowAddUserForm(true)}
            disabled={isSubmitting}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="19" y1="8" x2="19" y2="14"/>
              <line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
            Add User
          </button>
        </div>
      </div>

      <div className="applist-content-body">
        {loading ? (
          <div className="permissions-loading">
            <div className="permissions-loading-spinner"></div>
            <p>Loading organization permissions...</p>
          </div>
        ) : (
          <div className="permissions-content">
            {usersWithPermissions.length === 0 ? (
              <div className="permissions-empty-state">
                <div className="permissions-empty-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="m22 2-5 5"/>
                    <path d="m17 7 5-5"/>
                  </svg>
                </div>
                <h4>No Users Found</h4>
                <p>No users with permissions found for this organization.</p>
                <button
                  type="button"
                  className="permissions-add-first-user-btn"
                  onClick={() => setShowAddUserForm(true)}
                >
                  Add Your First User
                </button>
              </div>
            ) : (
              <>
                <div className="permissions-summary">
                  <div className="permissions-summary-card">
                    <div className="permissions-summary-icon permissions-read">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </div>
                    <div className="permissions-summary-content">
                      <h5>Read Access</h5>
                      <span className="permissions-summary-count">
                        {usersWithPermissions.filter(u => u.organizationRoles.read).length}
                      </span>
                    </div>
                  </div>

                  <div className="permissions-summary-card">
                    <div className="permissions-summary-icon permissions-write">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </div>
                    <div className="permissions-summary-content">
                      <h5>Write Access</h5>
                      <span className="permissions-summary-count">
                        {usersWithPermissions.filter(u => u.organizationRoles.write).length}
                      </span>
                    </div>
                  </div>

                  <div className="permissions-summary-card">
                    <div className="permissions-summary-icon permissions-admin">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 1L3 5v6c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12V5l-9-4z"/>
                        <path d="M9 12l2 2 4-4"/>
                      </svg>
                    </div>
                    <div className="permissions-summary-content">
                      <h5>Admin Access</h5>
                      <span className="permissions-summary-count">
                        {usersWithPermissions.filter(u => u.organizationRoles.admin).length}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="permissions-table-container">
                  <table className="permissions-table">
                    <thead>
                      <tr>
                        <th>User Email</th>
                        <th>Organization Roles</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersWithPermissions
                        .sort((a, b) => {
                          // Current user always at the top
                          if (a.email === currentUser?.email) return -1;
                          if (b.email === currentUser?.email) return 1;
                          // Rest sorted alphabetically
                          return a.email.localeCompare(b.email);
                        })
                        .map((user) => (
                          <tr key={user.email} className="permissions-table-row">
                            <td className="permissions-user-cell">
                              <div className="permissions-user-info">
                                <span className="permissions-user-email">{user.email}</span>
                              </div>
                            </td>
                            <td className="permissions-roles-cell">
                              <div className="permissions-role-toggles">
                                {(['read', 'write', 'admin'] as const).map((role) => (
                                  <button
                                    key={role}
                                    type="button"
                                    className={`permissions-role-toggle ${user.organizationRoles[role] ? getRoleBadgeColor(role) : 'permissions-badge-none'}`}
                                    onClick={() => handleRoleToggle(user.email, role, user.organizationRoles[role])}
                                    disabled={isSubmitting || user.email === currentUser?.email}
                                    title={user.email === currentUser?.email ? 'Cannot modify your own permissions' : `Click to ${user.organizationRoles[role] ? 'remove' : 'add'} ${role} access`}
                                  >
                                    {role.charAt(0).toUpperCase() + role.slice(1)}
                                    {user.organizationRoles[role] && (
                                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"/>
                                      </svg>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </td>
                            <td className="permissions-actions-cell">
                              <div className="permissions-user-actions">
                                {user.email !== currentUser?.email && (
                                  <button
                                    type="button"
                                    className="permissions-remove-user-btn"
                                    onClick={() => handleRemoveUser(user.email, user.organizationRoles)}
                                    disabled={isSubmitting}
                                    title="Remove all access"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"/>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUserForm && (
        <div className="modal-overlay">
          <div className="modal-container permissions-add-user-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Add User to {orgInfo.name}</h3> {/* UPDATED: Show org name in modal title */}
                <button 
                  type="button" 
                  className="modal-close"
                  onClick={() => setShowAddUserForm(false)}
                  disabled={isSubmitting}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <div className="permissions-add-user-form">
                  <div className="form-field">
                    <label>User Email Address</label>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="permissions-email-input"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-field">
                    <label>Assign Roles</label>
                    <div className="permissions-role-checkboxes">
                      {([
                        { 
                          role: 'read', 
                          title: 'Read Access', 
                          description: 'Can view organization data and apps but cannot make changes' 
                        },
                        { 
                          role: 'write', 
                          title: 'Write Access', 
                          description: 'Can create and edit apps, stores, and other organization content' 
                        },
                        { 
                          role: 'admin', 
                          title: 'Admin Access', 
                          description: 'Full access including user management and organization settings' 
                        }
                      ] as const).map(({ role, title, description }) => (
                        <label key={role} className="permissions-checkbox-label" data-role={role}>
                          <input
                            type="checkbox"
                            checked={newUserRoles[role]}
                            onChange={(e) => setNewUserRoles(prev => ({
                              ...prev,
                              [role]: e.target.checked
                            }))}
                            disabled={isSubmitting}
                          />
                          <div className="permissions-checkbox-content">
                            <span className="permissions-checkbox-title">{title}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="permissions-submit-btn"
                  onClick={handleAddUser}
                  disabled={isSubmitting || !newUserEmail.trim()}
                >
                  {isSubmitting ? 'Adding User...' : 'Add User'}
                </button>
                <button
                  type="button"
                  className="permissions-cancel-btn"
                  onClick={() => setShowAddUserForm(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PermissionsManagement;